<template>
  <v-container>
    <div v-if="loadingSenders" class="text-xs-center">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-alert :value="true" class="text-xs-center" outline type="error" v-if="loadingSendersError">
      <div>Não foi possível carregar os remetentes.</div>

      <v-btn flat @click="getSenders()"> Tentar novamente </v-btn>
    </v-alert>

    <v-layout align-center justify-start row v-if="senders.length">
      <v-flex shrink>
        <sender-selector
          :items="senders"
          v-model="selectedSender"
          return-object
          @input="searchTemplates"></sender-selector>
      </v-flex>
    </v-layout>

    <div v-if="senders.length && !selectedSender" class="mt-5 text-xs-center headline">
      Selecione um remetente
    </div>

    <div v-if="selectedSender" class="templates-filter">
      <v-layout align-center class="mb-4" justify-start row wrap>
        <v-text-field
          box
          class="pt-0"
          clearable
          hide-details
          placeholder="Pesquisar modelos"
          prepend-inner-icon="search"
          single-line
          v-model="search"></v-text-field>

        <v-btn
          :href="
            'https://portal.infobip.com/channels-and-numbers/channels/whatsapp/senders/' +
            selectedSender +
            '/templates/new'
          "
          color="accent"
          depressed
          fab
          target="_blank"
          title="Criar modelo no Portal da Infobip">
          <v-icon>add</v-icon>
        </v-btn>

        <v-btn
          :href="
            'https://portal.infobip.com/channels-and-numbers/channels/whatsapp/senders/' +
            selectedSender +
            '/templates'
          "
          icon
          target="_blank"
          title="Ver os modelos no Portal da Infobip">
          <v-icon>list</v-icon>
        </v-btn>

        <v-spacer></v-spacer>

        <v-subheader v-if="templates.length"> {{ templates.length }} modelos </v-subheader>

        <v-btn @click="searchTemplates(selectedSender)" icon title="Atualizar">
          <v-icon color="grey">refresh</v-icon>
        </v-btn>
      </v-layout>

      <div v-if="loadingTemplates" class="text-xs-center">
        <v-progress-circular color="primary" indeterminate></v-progress-circular>
      </div>

      <v-alert
        :value="true"
        class="text-xs-center"
        outline
        type="error"
        v-if="loadingTemplatesError">
        <div>Não foi possível carregar os modelos.</div>

        <v-btn flat @click="searchTemplates(selectedSender)"> Tentar novamente </v-btn>
      </v-alert>

      <v-data-table
        :headers="templatesHeaders"
        :hide-actions="true"
        :items="templates"
        :search="search"
        class="whatsapp-templates"
        no-data-text="Nenhum modelo encontrado"
        no-results-text="Nenhum modelo encontrado"
        v-if="!loadingTemplates && !loadingTemplatesError">
        <template v-slot:items="{ item }">
          <tr>
            <td class="name-and-body">
              <span class="name">
                {{ item.name }}
              </span>

              <div class="structure-body">
                <div
                  v-if="item.structure && item.structure.body.text"
                  :title="item.structure.body.text"
                  class="body-text">
                  {{ item.structure.body.text }}
                </div>
              </div>
            </td>

            <td>
              {{ item.category | whatsappTemplateCategory }}
            </td>

            <td>
              <span class="status" :class="[item.status]">
                {{ item.status | whatsappTemplateStatus }}
              </span>
            </td>

            <td>
              <QualityChip
                size="small"
                :quality="item.quality"
                :tooltip="'Qualidade ' + $options.filters.whatsappTemplateQuality(item.quality)"
              />
            </td>

            <td style="white-space: nowrap">
              <v-btn
                @click="templateSelected(item, selectedSender)"
                color="primary"
                :disabled="(item.initiatives && item.initiatives.length > 0) || item.language !== 'pt_BR'"
                depressed
                v-if="templateSelected">
                Importar
              </v-btn>

              <v-btn
              :href="
                  'https://portal.infobip.com/channels-and-numbers/channels/whatsapp/senders/' +
                  selectedSender +
                  '/templates/' +
                  item.id +
                  '/edit'
                "
                icon
                target="_blank"
                title="Editar modelo no portal da Infobip">
                <v-icon color="grey">edit</v-icon>
              </v-btn>
              <v-btn icon style="cursor: default;">
                  <v-tooltip 
                    v-if="(item.initiatives && item.initiatives.length > 1) || item.language !== 'pt_BR'"
                    bottom>
                    <template v-slot:activator="{ on }">
                      <v-icon 
                        v-on="on"
                        color="yellow darken-2">
                        mdi-alert-outline
                      </v-icon>
                    </template>
                    <span>
                      {{ item.language !== 'pt_BR'
                          ? 'Template em idioma diferente de Português (Brasil)'
                          : 'Iniciativa duplicada' }}
                    </span>
                  </v-tooltip>
                </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>
  </v-container>
</template>

<script>
import WhatsAppService from "@/services/whatsapp";
import SenderSelector from "@/components/senderSelector.vue";
import QualityChip from "@/components/ui/QualityChip.vue";

export default {
  props: {
    templateSelected: {
      type: Function,
      default: () => {},
    },
  },

  components: {
    SenderSelector,
    QualityChip,
  },

  data() {
    return {
      loadingSenders: false,
      loadingSendersError: false,
      selectedSender: null,
      sender: null,
      senders: [],
      loadingTemplates: false,
      loadingTemplatesError: false,
      search: null,
      templates: [],
      templatesHeaders: [
        { text: "Nome", value: "name", width: "100%" },
        { text: "Categoria", value: "category" },
        { text: "Status", value: "status" },
        { text: "Qualidade", value: "quality" },
        { text: "", value: "actions", sortable: false },
      ],
    };
  },

  created() {
    this.search = this.$route.query.search;
    this.selectedSender = this.$route.query.sender;

    this.getSenders();

    if (this.selectedSender) {
      this.searchTemplates(this.selectedSender);
    }
  },

  methods: {
    getSenders() {
      if (this.loadingSenders) {
        return;
      }
      this.loadingSenders = true;
      this.loadingSendersError = false;
      WhatsAppService.getSenders()
        .then((response) => {
          this.senders = [];
          response.objects.forEach((object) => {
            if (object.meta_waba_id === null) {
              return;
            }
            this.senders.push(object);
          });
          this.senders.sort((a, b) => {
            if (a.phone_number < b.phone_number) {
              return -1;
            }
            if (a.phone_number > b.phone_number) {
              return 1;
            }
            return 0;
          });
        })
        .catch((error) => {
          console.error("Não foi possível buscar os remetentes do WhatsApp", error);
          this.loadingSendersError = true;
        })
        .finally(() => {
          this.loadingSenders = false;
        });
    },

    searchTemplates(sender) {
      if (this.loadingTemplates || !sender.id) {
        return;
      }

      this.loadingTemplates = true;
      this.loadingTemplatesError = false;
      this.templates = [];

      WhatsAppService.searchTemplates(sender)
        .then((response) => {
          this.templates = response.objects;
        })
        .catch((error) => {
          console.error("Não foi possível buscar os templates do WhatsApp", error);
          this.loadingTemplatesError = true;
        })
        .finally(() => {
          this.loadingTemplates = false;
        });
    },
  },
};
</script>

<style lang="scss">

.tooltip-wrapper {
  display: inline-flex;
  align-items: center;
  margin-left: 8px; /* opcional para espaçamento */
}
.whatsapp-templates {
  table.v-table {
    tbody {
      td:first-child {
        padding-bottom: 16px !important;
      }
    }
  }

  .name {
    font-weight: bold;
  }

  .structure-body {
    position: relative;

    .body-text {
      color: gray;
      left: 0;
      overflow: hidden;
      position: absolute;
      right: 0;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.templates-filter {
  margin-top: 32px;
}
</style>
